import type { TimeRange } from '../api/generated/TimeRange'
import { addMinutes } from '../shifts/time'

/** Hours of a day, `HH:MM`; an end at or before the start is on the next day (`00:00`: midnight). */
export type Hours = { start: string; end: string }

export type DayAvailability = { kind: 'none' } | { kind: 'all' } | { kind: 'hours'; hours: Hours[] }

/** A volunteer's availability by day (`YYYY-MM-DD`) of the event. */
export type AvailabilityForm = Record<string, DayAvailability>

const midnight = (day: string) => `${day}T00:00:00`
const nextDay = (day: string) => addMinutes(midnight(day), 24 * 60).slice(0, 10)
const hoursOf = (local: string) => local.slice(11, 16)

/** The availability of each day, as the parts of the ranges falling within it. */
export function availabilityForm(availability: TimeRange[], days: string[]): AvailabilityForm {
  const form: AvailabilityForm = {}
  for (const day of days) {
    const start = midnight(day)
    const end = midnight(nextDay(day))
    const parts = availability
      .filter((range) => range.starts_at < end && range.ends_at > start)
      .map((range) => ({
        starts_at: range.starts_at > start ? range.starts_at : start,
        ends_at: range.ends_at < end ? range.ends_at : end,
      }))
    const first = parts[0]
    if (!first) form[day] = { kind: 'none' }
    else if (parts.length === 1 && first.starts_at === start && first.ends_at === end)
      form[day] = { kind: 'all' }
    else
      form[day] = {
        kind: 'hours',
        hours: parts.map((part) => ({
          start: hoursOf(part.starts_at),
          end: hoursOf(part.ends_at),
        })),
      }
  }
  return form
}

/** The ranges the API expects, in order; the server merges those that touch. */
export function availabilityRanges(form: AvailabilityForm): TimeRange[] {
  const ranges: TimeRange[] = []
  for (const day of Object.keys(form).sort()) {
    const availability = form[day]
    if (availability?.kind === 'all')
      ranges.push({ starts_at: midnight(day), ends_at: midnight(nextDay(day)) })
    if (availability?.kind === 'hours')
      for (const { start, end } of availability.hours) {
        if (!start || !end) continue
        const endDay = end > start ? day : nextDay(day)
        ranges.push({ starts_at: `${day}T${start}:00`, ends_at: `${endDay}T${end}:00` })
      }
  }
  return ranges
}

const formatHours = ({ start, end }: Hours) => `${start} – ${end === '00:00' ? 'minuit' : end}`

/** `Toute la journée`, `08:00 – 12:00, 14:00 – minuit` or `Indisponible`. */
export function describeDay(availability: DayAvailability | undefined): string {
  if (availability?.kind === 'all') return 'Toute la journée'
  if (availability?.kind === 'hours') return availability.hours.map(formatHours).join(', ')
  return 'Indisponible'
}
